import {
  JoorPayOrdersQuery,
  OrderEvent,
  OrderPriceModifier,
} from '__generated__/atlas-types'

import { wipeDateHours } from 'utils/dates'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import { formatReminderCell } from './joorPayOrders.configuration'
import {
  OrderStatuses,
  PaymentStatuses,
  PaymentTypes,
  RequestStatuses,
  TableColumns,
  TableRowData,
} from './joorPayOrders.types'

const customFeesAccumulator = (acc: number, customFee: OrderPriceModifier) => {
  return acc + Number(customFee?.factor ?? 0)
}

export const mapOrderEdgesToTableRows = (
  data: JoorPayOrdersQuery | undefined,
) =>
  data?.orders?.edges.map(
    ({ node }): TableRowData => ({
      // Table Related Data
      [TableColumns.OrderCreatedDate]: wipeDateHours(node.created) as Date,
      [TableColumns.RetailerName]:
        node.retailerProfile?.displayName ?? EMPTY_CELL_CONTENT,
      [TableColumns.PO]: node.referenceNumber ?? EMPTY_CELL_CONTENT,
      [TableColumns.PaymentStatus]: node.paymentInfo?.paymentStatus
        ? PaymentStatuses[node.paymentInfo.paymentStatus]
        : EMPTY_CELL_CONTENT,
      [TableColumns.Reminders]: formatReminderCell({
        dueDate: node.paymentInfo?.dueDate,
        lastNotifiedDate: node.paymentInfo?.lastNotifiedDate,
        paymentStatus: node.paymentInfo?.paymentStatus,
      }),
      [TableColumns.TotalDue]: Number(node.financials?.dueTotal.maxPrice ?? 0),
      [TableColumns.DueDate]: node.paymentInfo?.dueDate
        ? (wipeDateHours(node.paymentInfo?.dueDate) as Date)
        : undefined,
      [TableColumns.PaidAmount]: Number(node.paymentInfo?.paidAmount) ?? 0,
      [TableColumns.RemainingBalance]:
        Number(node.paymentInfo?.remainingBalance) ?? 0,
      [TableColumns.TotalUnits]: Number(node.financials?.totalUnits) ?? 0,
      [TableColumns.Currency]:
        node.priceType?.currency.code ?? EMPTY_CELL_CONTENT,
      [TableColumns.FirstRequest]: node.paymentInfo?.firstRequest
        ? (wipeDateHours(node.paymentInfo.firstRequest) as Date)
        : undefined,
      [TableColumns.LastTransaction]: node.paymentInfo?.lastTransaction
        ? (wipeDateHours(node.paymentInfo.lastTransaction) as Date)
        : undefined,
      [TableColumns.LastNotified]: node.paymentInfo?.lastNotifiedDate
        ? (wipeDateHours(node.paymentInfo.lastNotifiedDate) as Date)
        : undefined,
      [TableColumns.RequestStatus]: node.paymentInfo?.requestStatus
        ? RequestStatuses[node.paymentInfo.requestStatus]
        : EMPTY_CELL_CONTENT,
      [TableColumns.PaymentMethod]:
        node.paymentMethod?.name ?? EMPTY_CELL_CONTENT,
      [TableColumns.OrderStatus]: node.status
        ? OrderStatuses[node.status]
        : EMPTY_CELL_CONTENT,
      [TableColumns.TrackingNumber]: node.trackingNumber ?? EMPTY_CELL_CONTENT,
      [TableColumns.SalesRep]:
        node.salesRepresentative?.displayName ?? EMPTY_CELL_CONTENT,
      [TableColumns.Event]: node.event as OrderEvent,
      [TableColumns.ShipStart]:
        node.shipping.deliveryWindow?.startDate ?? undefined,
      [TableColumns.ShipComplete]:
        node.shipping.deliveryWindow?.completeDate ?? undefined,
      [TableColumns.Linesheet]: {
        id: node.collections.edges[0]?.node.id ?? '',
        name: node.collections.edges[0]?.node.name ?? '',
      },
      [TableColumns.TotalOrderValue]: Number(
        node.financials?.wholesaleTotal.maxPrice ?? 0,
      ),
      [TableColumns.SubtotalWholesale]: Number(
        node.financials?.subTotal.maxPrice ?? 0,
      ),
      [TableColumns.OrderDiscount]: Number(
        node.financials?.orderDiscount?.factor ?? 0,
      ),
      [TableColumns.ShippingFees]: Number(
        node.financials?.shippingFees?.factor ?? 0,
      ),
      [TableColumns.Taxes]: Number(node.financials?.tax?.factor ?? 0),
      [TableColumns.CustomFees]:
        node.financials?.customFees?.reduce(customFeesAccumulator, 0) ?? 0,
      // General Order Data needed for actions
      id: node.id,
      webOrderLookupId: node.webOrderLookupId,
      shippingEmail: node.shipping.address.email,
      retailerId: node.retailerProfile?.id,
      brandId: node.brand.id,
      brandName: node.brand.name,
      type: PaymentTypes.JoorPayment,
      billingFirstAddress: node.billing.address.firstAddress,
      billingSecondAddress: node.billing.address.secondAddress,
      billingCountry: node.billing.address.country,
      billingCity: node.billing.address.city,
      billingZip: node.billing.address.zip,
    }),
  ) ?? []

const DAYS_IN_MONTH = 30
export const getDateRangeFromNow = (monthBefore: number) => {
  const days = monthBefore * DAYS_IN_MONTH
  const now = new Date()
  const priorDate = new Date(new Date().setDate(now.getDate() - days))

  return {
    // Using toISOString assuming the BE works with UTC
    startDate: priorDate.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  }
}
